
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import SampleCode from '../../../components/docs/plaidoms/SampleCode';
export const metadata = {
  toc: true,
  layout: 'guide',
  description: "Learn how to add Plaid's Balance endpoints to your application to obtain current balance information."
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Add Balance to your app' subtitle='Use Balance to fetch real-time balance data' mdxType="Header" />
    <p>{`In this guide, we'll start from scratch and walk through how to use Balance to retrieve real-time balance information and assess ACH return risk. If you are already familiar with using Plaid and are set up to make calls to the Plaid API, make sure to note that you should `}<em parentName="p">{`not`}</em>{` include `}<inlineCode parentName="p">{`balance`}</inlineCode>{` in the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` products array, but you `}<em parentName="p">{`should`}</em>{` include `}<inlineCode parentName="p">{`signal`}</inlineCode>{`, if you are using Balance for an ACH risk assessment use case; you can then skip ahead to `}<a parentName="p" {...{
        "href": "#fetching-balance-data"
      }}>{`Fetching balance data`}</a>{`.`}</p>
    <h4 {...{
      "id": "get-plaid-api-keys-and-complete-application-and-company-profile"
    }}>{`Get Plaid API keys and complete application and company profile`}</h4>
    <p>{`If you don't already have one, you'll need to `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/signup"
      }}>{`create a Plaid developer account`}</a>{`. After creating your account, you can find your `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/keys"
      }}>{`API keys`}</a>{` under Build in the Plaid Dashboard.`}</p>
    <p>{`You will also need to complete your `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/settings/company/app-branding"
      }}>{`application profile`}</a>{` and `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/settings/company/profile"
      }}>{`company profile`}</a>{` in the Dashboard. The information in your profile will be shared with users of your application when they manage their connection on the `}<a parentName="p" {...{
        "href": "https://my.plaid.com"
      }}>{`Plaid Portal`}</a>{`. Your application profile and company profile must be completed before connecting to certain institutions in Production.`}</p>
    <h4 {...{
      "id": "install-and-initialize-plaid-libraries"
    }}>{`Install and initialize Plaid libraries`}</h4>
    <p>{`You can use our official server-side client libraries to connect to the Plaid API from your application:`}</p>
    <SampleCode.InstallClientLibraries />
    <p>{`After you've installed Plaid's client libraries, you can initialize them by passing in your `}<inlineCode parentName="p">{`client_id`}</inlineCode>{`, `}<inlineCode parentName="p">{`secret`}</inlineCode>{`, and the environment you wish to connect to (Sandbox or Production). This will make sure the client libraries pass along your `}<inlineCode parentName="p">{`client_id`}</inlineCode>{` and `}<inlineCode parentName="p">{`secret`}</inlineCode>{` with each request, and you won't need to explicitly include them in any other calls.`}</p>
    <SampleCode.InitializeClientLibraries />
    <h4 {...{
      "id": "create-an-item-in-link"
    }}>{`Create an Item in Link`}</h4>
    <p>{`Plaid Link is a drop-in module that provides a secure, elegant authentication flow
for each institution that Plaid supports. Link makes it secure and easy for users to
connect their bank accounts to Plaid. Note that these instructions cover Link on the web. For instructions on using Link within mobile apps, see the `}<a parentName="p" {...{
        "href": "/docs/link"
      }}>{`Link documentation`}</a>{`.`}</p>
    <p>{`Using Link, we will create a Plaid `}<em parentName="p">{`Item`}</em>{`, which is a Plaid term for a login at a financial institution. An Item is not the same as a financial institution account, although every account will be associated with an Item. For example, if a user has one login at their bank that allows them to access both their checking account and their savings account, a single Item would be associated with both of those accounts. If you want to customize Link's look and feel, you can do so from the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/link"
      }}>{`Dashboard`}</a>{`.`}</p>
    <p>{`If you are using Balance for a payments use case and you do not set the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/link/account-select"
      }}>{`Link Account Select UI`}</a>{` to "Single select", your UI flow when creating a payment must handle the scenario of an end user linking an Item with multiple accounts and allow them to specify which account to use for the payment.`}</p>
    <Callout mdxType="Callout">
      <p>{`In order to launch Link in Production, you must `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/link/data-transparency-v5"
        }}>{`select use cases for your Link customization`}</a>{`. This requirement is not enforced in Sandbox.`}</p>
    </Callout>
    <p>{`Before initializing Link, you will need to create a new `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` on the server side of your application.
A `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` is a short-lived, one-time use token that is used to authenticate your app with Link.
You can create one using the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` endpoint. Then, on the client side of your application, you'll need to initialize Link with the `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` that you just created.`}</p>
    <p>{`If using Balance for ACH return risk assessment, include `}<inlineCode parentName="p">{`signal`}</inlineCode>{` in the `}<inlineCode parentName="p">{`products`}</inlineCode>{` array, along with all other Plaid product(s) you will be using with Balance. `}</p>
    <p>{`For all other Balance use cases, omit `}<inlineCode parentName="p">{`signal`}</inlineCode>{`, but instead include the Plaid product(s) you will be using with Balance in the `}<inlineCode parentName="p">{`products`}</inlineCode>{` array.`}</p>
    <blockquote>
      <p parentName="blockquote"><inlineCode parentName="p">{`balance`}</inlineCode>{` cannot be included in the `}<inlineCode parentName="p">{`products`}</inlineCode>{` array when calling `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`. `}</p>
    </blockquote>
    <h5 {...{
      "id": "create-a-link_token"
    }}>{`Create a link_token`}</h5>
    <SampleCode.CreateLinkToken product='signal, auth' />
    <h5 {...{
      "id": "install-link-dependency"
    }}>{`Install Link dependency`}</h5>
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "#index.html",
        "#index.html": true
      }}>{`<head>
  <title>Connect a bank</title>
  <script src="https://cdn.plaid.com/link/v2/stable/link-initialize.js"></script>
</head>
`}</code></pre>
    <h5 {...{
      "id": "configure-the-client-side-link-handler"
    }}>{`Configure the client-side Link handler`}</h5>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "switcher=false #app.js",
        "switcher": "false",
        "#app.js": true
      }}>{`const linkHandler = Plaid.create({
  token: (await $.post('/create_link_token')).link_token,
  onSuccess: (public_token, metadata) => {
    // Send the public_token to your app server.
    $.post('/exchange_public_token', {
      public_token: public_token,
    });
  },
  onExit: (err, metadata) => {
    // Optionally capture when your user exited the Link flow.
    // Storing this information can be helpful for support.
  },
  onEvent: (eventName, metadata) => {
    // Optionally capture Link flow events, streamed through
    // this callback as your users connect an Item to Plaid.
  },
});

linkHandler.open();
`}</code></pre>
    <h4 {...{
      "id": "get-a-persistent-access_token"
    }}>{`Get a persistent access_token`}</h4>
    <p>{`Next, on the server side, we need to exchange our `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` for an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` and `}<inlineCode parentName="p">{`item_id`}</inlineCode>{`. The `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` will allow us to make authenticated calls to the Plaid API. Doing so is as easy as calling the `}<inlineCode parentName="p">{`/item/public_token/exchange`}</inlineCode>{` endpoint from our server-side handler. We'll use the client library we configured earlier to make the API call.`}</p>
    <p>{`Save the `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` and `}<inlineCode parentName="p">{`item_id`}</inlineCode>{` in a secure datastore, as they're used to access `}<inlineCode parentName="p">{`Item`}</inlineCode>{` data and identify `}<inlineCode parentName="p">{`webhooks`}</inlineCode>{`, respectively. The `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` will remain valid unless you actively choose to expire it via rotation or remove the corresponding Item via `}<inlineCode parentName="p">{`/item/remove`}</inlineCode>{`. The `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` should be stored securely, and never in client-side code. A `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` is a one-time use token with a lifetime of 30 minutes, so there is no need to store it.`}</p>
    <SampleCode.ExchangePublicToken />
    <h4 {...{
      "id": "fetching-balance-data"
    }}>{`Fetching Balance data`}</h4>
    <p>{`Now that the authentication step is out of the way, we can begin using authenticated endpoints from the Plaid API. `}</p>
    <p>{`There are two ways to use Balance: by using `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{`, or by using `}<inlineCode parentName="p">{`/accounts/balance/get`}</inlineCode>{`. `}</p>
    <p>{`If you are using Balance to evaluate a proposed ACH transaction for return risk, use `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{`.`}</p>
    <p>{`For all other use cases, use `}<inlineCode parentName="p">{`/accounts/balance/get`}</inlineCode>{`.`}</p>
    <h5 {...{
      "id": "using-signalevaluate"
    }}>{`Using /signal/evaluate`}</h5>
    <h6 {...{
      "id": "creating-a-balance-only-ruleset"
    }}>{`Creating a Balance-only ruleset`}</h6>
    <p>{`In the Dashboard, navigate to `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/signal/risk-profiles"
      }}><strong parentName="a">{`Signal->Rules`}</strong></a>{` to create a Balance-only ruleset. When you do, Plaid will pre-populate a suggested ruleset; you can either use it as-is or customize it. `}</p>
    <Callout mdxType="Callout">
      <p>{`In Sandbox, you will be offered a choice between Balance-only and Signal Transaction Scores–powered rulesets. To use Balance, select "Balance-only". In Production, Signal Transaction Scores rulesets will only be provided as an option if you are Production-enabled for the Signal Transaction Scores product.`}</p>
    </Callout>
    <h6 {...{
      "id": "getting-the-account_id"
    }}>{`Getting the account_id`}</h6>
    <p>{`If you are using `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{`, you will need to know the `}<inlineCode parentName="p">{`account_id`}</inlineCode>{` of the account that is being debited. You can get this in multiple ways, the simplest of which are: by using the `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback in Link (`}<inlineCode parentName="p">{`metadata.accounts[].id`}</inlineCode>{`) or by calling `}<inlineCode parentName="p">{`/accounts/get`}</inlineCode>{`. `}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "switcher=false #Method 1: The metadata object from Link onSuccess callback",
        "switcher": "false",
        "#Method": true,
        "1:": true,
        "The": true,
        "metadata": true,
        "object": true,
        "from": true,
        "Link": true,
        "onSuccess": true,
        "callback": true
      }}>{`{
  ...
  "accounts": [
    {
      "id": "ygPnJweommTWNr9doD6ZfGR6GGVQy7fyREmWy",
      "name": "Plaid Checking",
      "mask": "0000",
      "type": "depository",
      "subtype": "checking",
      "verification_status": null
    },
    {
      "id": "9ebEyJAl33FRrZNLBG8ECxD9xxpwWnuRNZ1V4",
      "name": "Plaid Saving",
      "mask": "1111",
      "type": "depository",
      "subtype": "savings"
    }
  ],
  ...
}
`}</code></pre>
    <MDXCodeBlock type='server' title='Method 2: Calling /accounts/get' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/accounts/get \\
  -H 'Content-Type: application/json' \\
  -d '{
    "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
    "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
    "access_token": String
  }'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const request: AccountsGetRequest = {
  access_token: ACCESS_TOKEN,
};
try {
  const response = await plaidClient.accountsGet(request);
  const accounts = response.data.accounts;
} catch (error) {
  // handle error
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`request = AccountsGetRequest(access_token=ACCESS_TOKEN)
response = client.accounts_get(request)
accounts = response['accounts']
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`AccountsGetRequest request = new AccountsGetRequest()
  .accessToken(ACCESS_TOKEN);
Response<AccountsGetResponse> response = client()
  .accountsGet(request)
  .execute();
List<AccountBase> accounts = response.body().getAccounts();
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`request = Plaid::AccountsGetRequest.new({ access_token: ACCESS_TOKEN })
response = client.accounts_get(request)
accounts = response.accounts
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`accountsGetRequest := plaid.NewAccountsGetRequest(accessToken)
accountsGetRequest.SetOptions(plaid.AccountsGetRequestOptions{
  AccountIds: &[]string{},
})

accountsGetResp, _, err := client.PlaidApi.AccountsGet(ctx).AccountsGetRequest(
  *accountsGetRequest,
).Execute()

accounts := accountsGetResp.GetAccounts()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`accountsGetRequest := &plaid.AccountsGetRequest{AccessToken: accessToken}
accountsGetRequest.Options = &plaid.AccountsGetRequestOptions{AccountIDs: []string{}}

accountsGetResp, err := client.Accounts.Get(ctx, accountsGetRequest)

accounts := accountsGetResp.GetAccounts()
`}</code></pre>
    </MDXCodeBlock>
    <p>{`A sample response is below (note that `}<inlineCode parentName="p">{`/accounts/get`}</inlineCode>{` returns balance data, but it is cached and not updated in real time):`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#/accounts/get response",
        "#/accounts/get": true,
        "response": true
      }}>{`{
  "accounts": [
    {
      "account_id": "blgvvBlXw3cq5GMPwqB6s6q4dLKB9WcVqGDGo",
      "balances": {
        "available": 100, 
        "current": 110,
        "iso_currency_code": "USD",
        "limit": null,
        "unofficial_currency_code": null
      },
      "holder_category": "personal",
      "mask": "0000",
      "name": "Plaid Checking",
      "official_name": "Plaid Gold Standard 0% Interest Checking",
      "subtype": "checking",
      "type": "depository"
    },
    {
      "account_id": "6PdjjRP6LmugpBy5NgQvUqpRXMWxzktg3rwrk",
      "balances": {
        "available": null,
        "current": 23631.9805,
        "iso_currency_code": "USD",
        "limit": null,
        "unofficial_currency_code": null
      },
      "mask": "6666",
      "name": "Plaid 401k",
      "official_name": null,
      "subtype": "401k",
      "type": "investment"
    }
  ],
  ...
`}</code></pre>
    <p>{`Once you have obtained both an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` and an `}<inlineCode parentName="p">{`account_id`}</inlineCode>{`, you can call `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` to evaluate the proposed transaction. If you do not specify a `}<inlineCode parentName="p">{`ruleset_key`}</inlineCode>{`, the transaction will be evaluated based on the ruleset named `}<inlineCode parentName="p">{`default`}</inlineCode>{`.`}</p>
    <MDXCodeBlock type='server' title='/signal/evaluate' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const eval_request = {
  access_token: 'access-sandbox-71e02f71-0960-4a27-abd2-5631e04f2175',
  account_id: '3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr',
  client_transaction_id: 'txn12345',
  amount: 123.45,
}

try {
  const eval_response = await plaidClient.signalEvaluate(eval_request);
  const core_attributes = eval_response.data.core_attributes;
} catch (error) {
  // handle error
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/signal/evaluate \\
-H 'Content-Type: application/json' \\
-d '{
   "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
   "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
   "access_token": "access-sandbox-71e02f71-0960-4a27-abd2-5631e04f2175",
   "account_id": "3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr",
   "client_transaction_id": "txn12345",
   "amount": 123.45
 }'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`
request = Plaid::SignalEvaluateRequest.new(
  {
    access_token: access_token,
    account_id: '3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr',
    client_transaction_id: 'txn12345',
    amount: 123.45,
  }
)
response = client.signal_evaluate(request)
result = response.ruleset.result
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`SignalEvaluateRequest request = new SignalEvaluateRequest()
  .accessToken("access-sandbox-71e02f71-0960-4a27-abd2-5631e04f2175")
  .accountId("3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr")
  .clientTransactionId("txn12345")
  .amount(123.45);

Response<SignalEvaluateResponse> response = plaidClient()
  .signalEvaluate(request)
  .execute();
RuleResult result = response.body().getRuleset().getResult();
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`request = SignalEvaluateRequest(
  access_token="access-sandbox-71e02f71-0960-4a27-abd2-5631e04f2175",
  account_id="3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr",
  client_transaction_id="txn12345",
  amount=123.45,
)
response = client.signal_evaluate(request)
result = response['ruleset']['result']
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`request := plaid.NewSignalEvaluateRequest(
  "access-sandbox-71e02f71-0960-4a27-abd2-5631e04f2175",
  "3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr",
  "txn12345",
  123.45,
)
response, _, err := client.PlaidApi.SignalEvaluate(ctx).SignalEvaluateRequest(*request).Execute()
ruleset := response.GetRuleset()
result := ruleset.GetResult()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`request := &plaid.SignalEvaluateRequest{
    AccessToken:         "access-sandbox-71e02f71-0960-4a27-abd2-5631e04f2175",
    AccountID:           "3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr",
    ClientTransactionID: "txn12345",
    Amount:              123.45,
}
response, err := client.Signal.Evaluate(ctx, request)
ruleset := response.GetRuleset()
result := ruleset.GetResult()
`}</code></pre>
    </MDXCodeBlock>
    <p>{`To determine which action to take, use the `}<inlineCode parentName="p">{`ruleset.result`}</inlineCode>{` value of `}<inlineCode parentName="p">{`ACCEPT`}</inlineCode>{`, `}<inlineCode parentName="p">{`REROUTE`}</inlineCode>{`, or `}<inlineCode parentName="p">{`REVIEW`}</inlineCode>{`. To learn more about these values, see `}<a parentName="p" {...{
        "href": "/docs/signal/signal-rules#using-signal-ruleset-results"
      }}>{`Signal rules`}</a>{`.`}</p>
    <p>{`You can also view the real-time current and available balances in `}<inlineCode parentName="p">{`core_attributes.current_balance`}</inlineCode>{` and `}<inlineCode parentName="p">{`core_attributes.available_balance`}</inlineCode>{`.`}</p>
    <h4 {...{
      "id": "reporting-outcomes"
    }}>{`Reporting outcomes`}</h4>
    <p>{`If the result was `}<inlineCode parentName="p">{`REVIEW`}</inlineCode>{`, report your final review decision to Plaid using `}<inlineCode parentName="p">{`/signal/decision/report`}</inlineCode>{` or by uploading a CSV. For more details, see `}<a parentName="p" {...{
        "href": "/docs/signal/reporting-returns"
      }}>{`Reporting decisions and returns`}</a>{`.`}</p>
    <MDXCodeBlock type='server' title='/signal/decision/report' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const decision_report_request = {
  client_transaction_id: 'txn12345',
  initiated: true,
  days_funds_on_hold: 3,
};

try {
  const decision_report_response = await plaidClient.signalDecisionReport(
    decision_report_request,
  );
  const decision_request_id = decision_report_response.data.request_id;
} catch (error) {
  // handle error
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/signal/decision/report \\
-H 'content-type: application/json' \\
 -d '{
   "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
   "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
   "client_transaction_id": "txn12345",
   "initiated": true
 }'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`request = Plaid::SignalDecisionReportRequest.new(
  {
    client_transaction_id: 'txn12345',
    initiated: true,
    days_funds_on_hold: 3
  }
)
response = client.signal_decision_report(request)
request_id = response.request_id
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`SignalDecisionReportRequest request = new SignalDecisionReportRequest()
  .clientTransactionId("txn12345")
  .initiated(true)
  .daysFundsOnHold(3);

Response<SignalDecisionReportResponse> response = plaidClient()
  .signalDecisionReport(request)
  .execute();
String requestID = response.body().getRequestId();
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`request = SignalDecisionReportRequest(
  client_transaction_id="txn12345",
  initiated=True,
  days_funds_on_hold=3,
)
response = client.signal_decision_report(request)
request_id = response['request_id']
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`request := plaid.NewSignalDecisionReportRequest(
  "txn12345",
  true,
)
request.SetDaysFundsOnHold(3)
response, _, err := client.PlaidApi.SignalDecisionReport(ctx).SignalDecisionReportRequest(*request).Execute()
request_id := response.GetRequestId()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`request := &plaid.SignalDecisionReportRequest{
    ClientTransactionID: "txn12345",
    Initiated:           true,
}
request.DaysFundsOnHold = plaid.Int(3)
response, err := client.Signal.ReportDecision(ctx, request)
request_id := response.GetRequestID()
`}</code></pre>
    </MDXCodeBlock>
    <p>{`If you allow a transfer that does end up returned, report that result back to Plaid as well. You can do this by calling `}<inlineCode parentName="p">{`/signal/return/report`}</inlineCode>{` or by uploading a CSV. For more details, see `}<a parentName="p" {...{
        "href": "/docs/signal/reporting-returns"
      }}>{`Reporting decisions and returns`}</a>{`.`}</p>
    <MDXCodeBlock type='server' title='/signal/return/report' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const return_report_request = {
  client_transaction_id: 'txn12345',
  return_code: 'R01',
};

try {
  const return_report_response = await plaidClient.signalReturnReport(
    return_report_request,
  );
  const request_id = return_report_response.data.request_id;
  console.log(request_id);
} catch (error) {
  // handle error
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/signal/return/report \\
-H 'content-type: application/json' \\
-d '{
   "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
   "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
   "client_transaction_id": "txn12345",
   "return_code": "R01"
 }'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`request = Plaid::SignalReturnReportRequest.new(
  {
    client_transaction_id: 'txn12345',
    return_code: 'R01'
  }
)
response = client.signal_return_report(request)
request_id = response.request_id
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`SignalReturnReportRequest request = new SignalReturnReportRequest()
  .clientTransactionId("txn12345")
  .returnCode("R01");

Response<SignalReturnReportResponse> response = client()
  .signalReturnReport(request)
  .execute();

String requestId = response.body().getRequestId();
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`request = SignalReturnReportRequest(
  client_transaction_id="txn12345",
  return_code="R01",
)
response = client.signal_return_report(request)
request_id = response['request_id']
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`request := plaid.NewSignalReturnReportRequest(
  "txn12345",
  "R01",
)
response, _, err := client.PlaidApi.SignalReturnReport(ctx).SignalReturnReportRequest(*request).Execute()
request_id := response.GetRequestId()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`request := &plaid.SignalReturnReportRequest{
    ClientTransactionID: "txn12345",
    ReturnCode:          "R01",
}
response, err := client.Signal.ReportReturn(ctx, request)
request_id := response.GetRequestID()
`}</code></pre>
    </MDXCodeBlock>
    <h5 {...{
      "id": "using-accountsbalanceget"
    }}>{`Using /accounts/balance/get`}</h5>
    <p>{`For more detailed information on the schema returned, see `}<inlineCode parentName="p">{`/accounts/balance/get`}</inlineCode>{`.`}</p>
    <MDXCodeBlock type='server' title='/accounts/balance/get' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const { AccountsBalanceGetRequest } = require('plaid');

// Pull real-time balance information for each account associated
// with the Item
const request: AccountsBalanceGetRequest = {
  access_token: accessToken,
};
try {
  const response = await plaidClient.accountsBalanceGet(request);
  const accounts = response.data.accounts;
} catch (error) {
  // handle error
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/accounts/balance/get \\
-H 'Content-Type: application/json' \\
-d '{
   "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
   "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "access_token": String,
  "options": {
    "account_ids": [String]
  }
}'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`# Pull real-time balance information for each account associated
# with the Item
request = Plaid::AccountsBalanceGetRequest.new({ access_token: access_token })
response = client.accounts_balance_get(request)
accounts = response.accounts
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`import com.plaid.client.model.AccountsBalanceGetRequest;
import com.plaid.client.model.AccountsGetResponse;

// Pull real-time balance information for each account associated
// with the Item
AccountsBalanceGetRequest request = new AccountsBalanceGetRequest()
  .accessToken(accessToken);

Response<AccountsGetResponse> response = client()
  .accountsBalanceGet(request)
  .execute();
List<AccountBase> accounts = response.body().getAccounts();
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`from plaid.model.accounts_balance_get_request import AccountsBalanceGetRequest

# Pull real-time balance information for each account associated
# with the Item
request = AccountsBalanceGetRequest(access_token=access_token)
response = client.accounts_balance_get(request)
accounts = response['accounts']
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`import (
    "context"
)

balancesGetResp, _, err := client.PlaidApi.AccountsBalanceGet(ctx).AccountsBalanceGetRequest(
  *plaid.NewAccountsBalanceGetRequest(accessToken),
).Execute()

accounts := balancesGetResp.GetAccounts()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`import (
    "context"
)

balancesGetResp, err := client.Accounts.GetBalance(ctx, &plaid.AccountsBalanceGetRequest{AccessToken: accessToken})

accounts := balancesGetResp.GetAccounts()
`}</code></pre>
    </MDXCodeBlock>
    <p>{`Example response data is below.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Balance sample response",
        "#Balance": true,
        "sample": true,
        "response": true
      }}>{`{
  "accounts": [
    {
      "account_id": "BxBXxLj1m4HMXBm9WZZmCWVbPjX16EHwv99vp",
      "balances": {
        "available": 100,
        "current": 110,
        "iso_currency_code": "USD",
        "limit": null,
        "unofficial_currency_code": null
      },
      "mask": "0000",
      "name": "Plaid Checking",
      "official_name": "Plaid Gold Standard 0% Interest Checking",
      "subtype": "checking",
      "type": "depository"
    },
    {
      "account_id": "dVzbVMLjrxTnLjX4G66XUp5GLklm4oiZy88yK",
      "balances": {
        "available": null,
        "current": 410,
        "iso_currency_code": "USD",
        "limit": 2000,
        "unofficial_currency_code": null
      },
      "mask": "3333",
      "name": "Plaid Credit Card",
      "official_name": "Plaid Diamond 12.5% APR Interest Credit Card",
      "subtype": "credit card",
      "type": "credit"
    },
    {
      "account_id": "Pp1Vpkl9w8sajvK6oEEKtr7vZxBnGpf7LxxLE",
      "balances": {
        "available": null,
        "current": 65262,
        "iso_currency_code": "USD",
        "limit": null,
        "unofficial_currency_code": null
      },
      "mask": "7777",
      "name": "Plaid Student Loan",
      "official_name": null,
      "subtype": "student",
      "type": "loan"
    }
  ],
  "item": {
    "available_products": [
      "balance",
      "credit_details",
      "identity",
      "investments"
    ],
    "billed_products": ["assets", "auth", "liabilities", "transactions"],
    "consent_expiration_time": null,
    "error": null,
    "institution_id": "ins_3",
    "item_id": "eVBnVMp7zdTJLkRNr33Rs6zr7KNJqBFL9DrE6",
    "webhook": "https://www.genericwebhookurl.com/webhook"
  },
  "request_id": "qk5Bxes3gDfv4F2"
}
`}</code></pre>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "get-plaid-api-keys-and-complete-application-and-company-profile",
        level: 4,
        title: "Get Plaid API keys and complete application and company profile",
      },
{
        id: "install-and-initialize-plaid-libraries",
        level: 4,
        title: "Install and initialize Plaid libraries",
      },
{
        id: "create-an-item-in-link",
        level: 4,
        title: "Create an Item in Link",
      },
{
        id: "get-a-persistent-access_token",
        level: 4,
        title: "Get a persistent access_token",
      },
{
        id: "fetching-balance-data",
        level: 4,
        title: "Fetching Balance data",
      },
{
        id: "reporting-outcomes",
        level: 4,
        title: "Reporting outcomes",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
